/** Shared helpers for the Fleet Vehicles page (List + Map).
 *
 * Status metadata mirrors `demo/lib/fleet/canonical.py::VehicleStatus`. Vehicles
 * carry no `heading` (only FleetCache does), so map markers are non-directional
 * circles coloured by route (outer) + status (centre dot) rather than arrows.
 */
import type { ChipProps } from '@mui/material';

export const DEFAULT_MARKER_COLOR = '#1976d2';

export interface StatusMeta {
  label: string;
  color: ChipProps['color'];
  hex: string;
}

const STATUS_META: Record<string, StatusMeta> = {
  moving: { label: 'Moving', color: 'success', hex: '#2e7d32' },
  stopped: { label: 'Stopped', color: 'default', hex: '#616161' },
  idle: { label: 'Idle', color: 'warning', hex: '#ed6c02' },
  offline: { label: 'Offline', color: 'error', hex: '#d32f2f' },
  decommissioned: { label: 'Decommissioned', color: 'default', hex: '#9e9e9e' },
};

/** Canonical statuses (mirror the backend enum) — the map's status multi-select. */
export const VEHICLE_STATUSES = Object.keys(STATUS_META);

export function statusMeta(status: string | null | undefined): StatusMeta {
  return (status && STATUS_META[status]) || { label: status || '—', color: 'default', hex: '#9e9e9e' };
}

/** Autorefresh dropdown options; `0` = off (→ refetchInterval false). */
export const AUTOREFRESH_OPTIONS: { label: string; value: number }[] = [
  { label: 'Off', value: 0 },
  { label: '10s', value: 10_000 },
  { label: '30s', value: 30_000 },
  { label: '1m', value: 60_000 },
];

export function refetchIntervalOf(ms: number): number | false {
  return ms > 0 ? ms : false;
}

export function fmtTs(ts: string | null | undefined): string {
  if (!ts) return 'never';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString();
}

export function formatAge(ts: string | null | undefined): string {
  if (!ts) return 'unknown';
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return 'unknown';
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function googleMapsLink(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/** Non-directional vehicle marker: route-coloured disc with a status centre dot. */
export function buildVehicleMarkerSvg(routeColor: string, statusHex: string): string {
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26">
  <circle cx="13" cy="13" r="10" fill="${routeColor}" stroke="#ffffff" stroke-width="2"/>
  <circle cx="13" cy="13" r="3.5" fill="${statusHex}"/>
</svg>`.trim();
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}
